import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import ContentEditable from 'react-contenteditable';
import AutosizeInput from 'react-input-autosize';

import { fontkoKR } from '@mirinae/react-ui';
import { UserAgent } from '@mirinae/js-utils';

import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { fontDefault, colorMap, styleMap } from '@mirinae/lessons/modules/defines/styles';

import leftArrow from '@mirinae/lessons/assets/images/leftArrow.png';
import rightArrow from '@mirinae/lessons/assets/images/rightArrow.png';

const isMobile = !UserAgent.os.isOthers;

const PhraseWrapper = styled.div`
    display: flex;                                
    flex-direction: row;
    font-style: normal;
    min-width: fit-content;
    ${props => (props.language === 'ko' ? (`
        font-family: ${fontkoKR};
        font-weight: ${props.phraseStyle.koFontWeight};
        font-size: ${parseInt(props.phraseStyle.koreanFontSize, 10) * props.fontScale}px;
    `) : (`
        font-family: ${fontDefault};
        font-weight: ${props.phraseStyle.enFontWeight};
        font-size: ${parseInt(props.phraseStyle.englishFontSize, 10) * props.fontScale}px;
    `))};
    text-align: ${props => props.phraseStyle.align || 'left'};
    padding-bottom: 4px;
    word-wrap: break-word;
    word-break: keep-all;
    display: inline-block;
    ${props => props.phraseStyle.visibility && (`visibility: ${props.phraseStyle.visibility};`)}
    ${props => (props.phraseStyle.list && `
        :before {
            content: '';
            display: inline-block;
            width: 3px;
            height: 3px;
            margin: 0 5px 3px 4px;
            border-radius: 50%;
            background-color: #3C3C3B;
            vertical-align: middle;
        }
    `)}
  
    input {
        ${props => (props.language === 'ko' ? (`
            font-family: ${fontkoKR};
            font-weight: ${props.phraseStyle.koFontWeight};
            font-size: ${parseInt(props.phraseStyle.koreanFontSize, 10) * props.fontScale}px;
        `) : (`
            font-family: ${fontDefault};
            font-weight: ${props.phraseStyle.enFontWeight};
            font-size: ${parseInt(props.phraseStyle.englishFontSize, 10) * props.fontScale}px;
        `))};
        text-align: ${props => props.phraseStyle.align || 'left'};
    }
`;

const PhraseColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${props => (props.phraseStyle.align === 'left' ? 'start' : 'center')};
    ${props => props.phraseStyle.padding && (`padding: ${props.phraseStyle.padding};`)};
`;

const WordWrapper = styled.div`
    position: relative;
    display: inline;
`;

const Word = styled.span`
    // margin: 0 2px 0 2px;
    color: ${props => colorMap[props.phraseStyle && props.phraseStyle.deselected ? 'deselected'
        : props.phraseStyle && props.phraseStyle.hover ? 'hover' 
            : props.phraseStyle && props.phraseStyle.dim ? 'dim' 
                : props.part.color]};
    padding-left:  ${props => (props.part.color === 'space' ? '0.25em' : '0')};
    ${props => styleMap[props.part.color] && styleMap[props.part.color]};
    ${props => props.phraseStyle.hover && `color: ${colorMap.hover} !important;`}
`;

// const TypeInWord = styled(ContentEditable)`
// const TypeInWord = styled.input`
const TypeInWord = styled(AutosizeInput)`
    display: inline-block;
    border-radius: 10px;
    border: 1px solid ${props => (props.incorrect ? '#FF8826' : '#C4C4C4')};
    background: #FFF;
    padding: 10px 20px;
    margin: 0px 5px 10px;
    font-weight: 400;
    min-width: 115px;
  
    input {
        border: none;
        min-width: 60px;
        font-weight: 500;
        color: ${props => (props.showAsHint ? '#dfdfdf' : '#525252')};
    }
  
    &:focus {
        border-color: ${props => (props.incorrect ? '#FF8826' : '#48A9A6')};
    }
`;

const Annotation = styled.div`
    position: absolute;
    bottom: 83px;
    ${(props) => (props.pos === 'left' ? 'right: 30px;' : 'left: 30px;')};
    font-family: ${fontDefault};
    font-size: 14px;
    font-weight: 500;
    width: fit-content;
    color: #939393;
`;

const Arrow = styled.img`
    position: absolute;
    bottom: 44px;
    transform: scale(1, -1);
    opacity: 0.25;
    left: ${props => (props.pos === 'left' ? -20 : 20)}px;);
    content: url(${props => (props.pos === 'left' ? leftArrow : rightArrow)});
`;

const PhraseWord = ({ part, phraseStyle, theme = null, onTypeIn, parts }) => {
    const [typeIn, setTypeIn] = useState('');

    const pos = () => (part.command[0] === '<' ? 'left' : 'right');

    const typeInChange = (e) => {
        setTypeIn(e.currentTarget.value);
        if (onTypeIn)
            onTypeIn(e.currentTarget.value, part, e);
    };

    const typeInMouseDown = (e) => {
        if (part.showHint) {
            e.currentTarget.value = '';
            setTypeIn('');
            part.showHint = false;
            part.typeIn = '';
        }
    };

    const resizeInput = (e) => {
        e.currentTarget.style.size = e.currentTarget.value.length; // 'fit-content'; // `calc(${e.currentTarget.value.length}ch + 40px)`;
    };

    return (
        <WordWrapper>
            { part.command && (
                <>
                    <Arrow part={part} pos={pos()} />
                    <Annotation part={part} pos={pos()}>{part.command.replace(/[<>]/, '')}</Annotation>
                </>
            )}
            { part.color === 'typeIn' ? (
                <TypeInWord
                    type="text" phraseStyle={phraseStyle} spellCheck={false} value={(part.showHint && part.value) || typeIn || ''}
                    incorrect={part.incorrect} onChange={typeInChange} showAsHint={part.showHint}
                    onInput={resizeInput} size={6} onMouseDown={typeInMouseDown}
                    ref={ref => part.ref = ref}
                />
            ) : (
                <Word className="phrase-word" part={part} phraseStyle={phraseStyle} theme={theme}>{ part.value }</Word>
            )}
        </WordWrapper>
    );
};

export const FormattedPhrase = ({ parts, phraseStyle = {}, language, theme = null, onTypeIn, typeInIncorrect }) => {
    const textRef = useRef(null);
    const [fontScale, setFontScale] = useState(1);
    const computedStyle = { ...{ koFontWeight: 'bold', koreanFontSize: '38px', enFontWeight: '600', englishFontSize: '16px', align: 'center' }, ...phraseStyle };

    useEffect(() => {
        // adapt font-size if phrase wraps
        if (phraseStyle.adaptiveFontScale && textRef.current) {
            const koFontSize = parseInt(phraseStyle.koreanFontSize, 10);
            const enFontSize = parseInt(phraseStyle.englishFontSize, 10);
            if (fontScale === 1 && textRef.current.offsetHeight > 2 * (language === 'ko' ? koFontSize : enFontSize)) { // heuristic
                // reduce font-size if we wrap
                setFontScale(phraseStyle.adaptiveFontScale);
            }
        }
    }, []);

    return (
        <PhraseWrapper ref={textRef} className="phrase-wrapper" language={language} phraseStyle={computedStyle} fontScale={fontScale} theme={theme}>
            { parts.filter(p => !p.color.match(/separator|separate|merge/)).map((part, i) => (
                <PhraseWord key={i} phraseStyle={computedStyle} part={part} theme={theme} onTypeIn={onTypeIn} parts={parts} />
            ))}
        </PhraseWrapper>
    );
};

export const FormattedKoPhrase = ({ phrase, phraseStyle, theme = null, onTypeIn, typeInIncorrect }) => (
    phrase.hiddenParts && phrase.hiddenParts.includes('koreanParts') ? null
        : <FormattedPhrase parts={phrase.parts.koreanParts} phraseStyle={phraseStyle} language="ko" theme={theme} onTypeIn={onTypeIn} typeInIncorrect={typeInIncorrect} />
);

export const FormattedEnPhrase = ({ phrase, phraseStyle }) => (
    !phrase.hiddenParts || !phrase.hiddenParts.includes('englishParts') ? <FormattedPhrase parts={phrase.parts.englishParts} phraseStyle={phraseStyle} language="en" /> : null
);

export const FormattedKoEnPhrase = ({ phrase, phraseStyle }) => {
    const computedStyle = {
        ...{ koFontWeight: 'bold', koreanFontSize: '38px', enFontWeight: '400', englishFontSize: '16px' },
        ...(phraseStyle || {}),
        ...(isMobile ? ((phrase.mobile && phrase.mobile.style) || {}) : (phrase.style || {})),
    };

    return (
        <PhraseColumn className="phrase-column" phraseStyle={computedStyle}>
            <FormattedKoPhrase phrase={phrase} phraseStyle={computedStyle} />
            <FormattedEnPhrase phrase={phrase} phraseStyle={computedStyle} />
        </PhraseColumn>
    );
};

export const ListedKoPhrase = ({ phrase, phraseStyle }) => {
    const computedStyle = {
        ...{ koFontWeight: 'bold', koreanFontSize: '38px', enFontWeight: '400', englishFontSize: '16px' },
        ...(phraseStyle || {}),
        ...(isMobile ? ((phrase.mobile && phrase.mobile.style) || {}) : (phrase.style || {})),
    };

    return (
        <PhraseColumn className="phrase-column" phraseStyle={computedStyle}>
            <FormattedKoPhrase phrase={phrase} phraseStyle={computedStyle} theme="list" />
        </PhraseColumn>
    );
};
export const VariantTableKoPhrase = ({ phrase, phraseStyle }) => {
    const computedStyle = {
        ...{ koFontWeight: 'bold', koreanFontSize: '24px', enFontWeight: '400', englishFontSize: '24px' },
        ...(phraseStyle || {}),
        ...(isMobile ? ((phrase.mobile && phrase.mobile.style) || {}) : (phrase.style || {})),
    };

    return (
        <PhraseColumn className="phrase-column" phraseStyle={computedStyle}>
            <FormattedKoPhrase phrase={phrase} phraseStyle={computedStyle} theme="variantTable" />
            <FormattedEnPhrase phrase={phrase} phraseStyle={computedStyle} />
        </PhraseColumn>
    );
};

export const EnglishPhrase = ({ element }) => {
    return (
        <Element element={element}>
            <FormattedEnPhrase phrase={element.fieldsByName.phrase} phraseStyle={element.style} />
        </Element>
    );
};

export const KoreanPhrase = ({ element }) => {
    return (
        <Element element={element}>
            <FormattedKoPhrase phrase={element.fieldsByName.phrase} phraseStyle={element.style} />
        </Element>
    );
};

const Phrase = ({ element }) => {
    return (
        <Element element={element}>
            <FormattedKoEnPhrase phrase={element.fieldsByName.phrase} phraseStyle={isMobile && element.mobile?.style ? element.mobile.style : element.style} />
        </Element>
    );
};

export default Phrase;
